import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Center, Flex, Loader } from '@mantine/core';
import { DatePicker } from '@mantine/dates';

import useExternalApiService from '../../../../../helpers/ExternalApiService';
import { store } from 'stores/Store';
import { ReactComponent as AddRouteIcon } from 'assets/addRoute.svg';
import { ReactComponent as CheckInIcon } from "assets/check_in.svg";

import styles from './FollowUpsTab.module.scss';

interface Props {
  onClickPractitioner: (practitioner: any) => void;
  onClickPractitionerCheckin: (practitioner: any) => void;
  onPractitionerAddedToRoute: () => void;
}

const FollowUpsTab = observer(({ onClickPractitioner, onClickPractitionerCheckin, onPractitionerAddedToRoute }: Props) => {
  const { postData } = useExternalApiService(process.env.REACT_APP_EAI_URL);

  const selectedDate = store.selectedFollowUpDate;
  const practitioners = store.practitionersInFollowUpDate;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submitAbortControllerRef = useRef<AbortController | null>(null);

  // TODO: Consider pagination if there are many follow-ups
  // TODO: Consider caching follow-ups for previously selected dates (maybe using Tanstack Query or SWR)
  const loadFollowUps = useCallback(async () => {
    setLoading(true);

    const abortController = new AbortController();
    submitAbortControllerRef.current = abortController;

    try {
      const response = await postData(
        '/api/get_practitioners_for_chat',
        {
          fmc_email: store.impersonatedUser,
          page: 1,
          limit: 50,
          alias: store.impersonatedUserAlias,
          'follow_up_date': selectedDate,
        },
        abortController.signal,
      );

      store.setPractitionersInFollowUpDate(response?.practitioners ?? []);
      setLoading(false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }

      setLoading(false);
      console.error('Error fetching follow-ups:', error);
    }
  }, [selectedDate]);

  const handleCustomDateChange = (val: string | null) => {
    store.setFollowUpDateSearch(val);
    store.setPractitionersInFollowUpDate([]);
  };

  const handleCompleteFollowUp = (practitionerId: number) => async () => {
    await postData(`/api/practitioner/${practitionerId}/followup/complete`);

    loadFollowUps();
  };

  useEffect(() => {
    if (!selectedDate || practitioners.length > 0) {
      return;
    }

    loadFollowUps();

    return () => {
      submitAbortControllerRef.current?.abort();
      submitAbortControllerRef.current = null;
      setLoading(false);
    };
  }, [loadFollowUps, practitioners.length, selectedDate]);

  return (
    <Box p={{ base: 0, sm: 10 }}>
      <Flex justify="center" p={10}>
        <DatePicker
          value={selectedDate}
          onChange={handleCustomDateChange}
          hideOutsideDates
          firstDayOfWeek={0}
        />
      </Flex>
      {loading ? (
        <Center style={{ height: '200px' }}>
          <Loader size="lg" />
        </Center>
      ) : (
        practitioners.length === 0 ? (
          <Center style={{ height: '200px' }}>
            No follow-ups scheduled for the selected date.
          </Center>
        ) : (
          practitioners.map((practitioner) => (
            <Flex
              key={practitioner.id}
              mb={10}
              gap={{ base: 5, sm: 10 }}
              className={styles.followUpItem}
              justify="space-between"
              align="center"
            >
              <Flex align="center" gap={{ base: 5, sm: 10 }}>
                <Button
                  type="button"
                  color="#58E487"
                  disabled={submitting}
                  loading={submitting}
                  classNames={{ root: styles.doneButton }}
                  onClick={handleCompleteFollowUp(practitioner.id)}
                >
                  Done
                </Button>
                <div onClick={() => onClickPractitioner(practitioner)} style={{ cursor: 'pointer' }}>
                  <div className={styles.practitionerName}>{practitioner.name}</div>
                  <div className={styles.address}>{practitioner.addresses && practitioner.addresses.length > 0 ? practitioner.addresses[0].address : 'No address available'}</div>
                </div>
              </Flex>
              <Flex align="center" gap={{ base: 5, sm: 10 }}>
                <Button
                  type="button"
                  variant="transparent"
                  disabled={submitting}
                  loading={submitting}
                  classNames={{ root: styles.actionButton, label: styles.actionButtonLabel }}
                  onClick={() => onClickPractitionerCheckin(practitioner)}
                >
                  <CheckInIcon stroke="#4F7D9C" width={30} height={30} />
                  <div>Check-In</div>
                </Button>
                <Button
                  type="button"
                  variant="transparent"
                  disabled={submitting}
                  loading={submitting}
                  classNames={{ root: styles.actionButton, label: styles.actionButtonLabel }}
                  onClick={() => {
                    store.addToRoute(practitioner);
                    onPractitionerAddedToRoute();
                  }}
                >
                  <AddRouteIcon />
                  <div>Add to Route</div>
                </Button>
              </Flex>
            </Flex>
          ))
        )
      )}
    </Box>
  );
});

export default FollowUpsTab;
